import React, { useEffect, useRef, useState } from 'react';
import './attachFile.scss';
import Button from 'components/atoms/Button';
import PropTypes from 'prop-types';

const AttachFile = ({
  multiple = true,
  onFilesChange = () => {},
  submitAttachment,
  documentAttached = false,
  handleAttchDocument = () => {},
  submit = false,
  handleSubmit = () => {},
}) => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = newFiles => {
    const incoming = Array.from(newFiles);
    const existingNames = new Set(files.map(f => f.name));
    const uniqueIncoming = incoming.filter(f => !existingNames.has(f.name));

    const fileList = multiple
      ? [...files, ...uniqueIncoming]
      : uniqueIncoming.slice(0, 1);

    setFiles(fileList);
    if (onFilesChange) onFilesChange(fileList);
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
    if (handleSubmit) {
      handleSubmit(false);
    }
  };

  const handleFileChange = e => {
    handleFiles(e.target.files);
    handleSubmit(false);
  };

  const removeFile = index => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    if (onFilesChange) onFilesChange(updatedFiles);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fileToBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.replace(
          new RegExp('^data:.*;base64,', 'gm'),
          '',
        );
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSubmitAttachments = async () => {
    try {
      if (!files || files.length === 0) return;

      const payloads = await Promise.all(
        files.map(async file => ({
          AttachmentContentType: file.type || 'application/octet-stream',
          AttachmentFile: await fileToBase64(file),
          AttachmentFilename: file.name,
        })),
      );

      if (submitAttachment) {
        submitAttachment(payloads);
      }
    } catch (error) {
      console.error('Error converting files to Base64:', error);
    }
  };

  useEffect(() => {
    if (files.length > 0 && !submit) {
      handleAttchDocument(true);
    } else if (files.length > 0 && submit) {
      setFiles([]);
    } else {
      handleAttchDocument(false);
    }

    if (files.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [files, submit]);

  return (
    <div className="attachment__container">
      <div
        className={`attach-file ${isDragging ? 'dragging' : ''}`}
        role="button"
        tabIndex={0}
        aria-label="Ladda upp filer"
        onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => {
          if (fileInputRef.current) {
            fileInputRef.current.click();
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (fileInputRef.current) {
              fileInputRef.current.click();
            }
          }
        }}
      >
        <input
          type="file"
          multiple={multiple}
          ref={fileInputRef}
          onChange={handleFileChange}
          hidden
        />
        <div className="attach-file__content">
          <p className="attach-file__text">
            {isDragging
              ? 'Släpp dina filer här...'
              : 'Dra hit filer eller klicka för att ladda upp'}
          </p>
        </div>

        {files.length > 0 && (
          <ul className="attach-file__list">
            {files.map((file, index) => (
              <li key={file.name} className="attach-file__item">
                <span className="attach-file__name">{file.name}</span>
                <div className="attach-file__detail">
                  <span className="attach-file__size">
                    ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                  <button
                    type="button"
                    className="attach-file__remove"
                    onClick={e => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    onKeyDown={e => {
                      if(e.key === "Enter") {
                        e.preventDefault();
                        removeFile(index);
                      }
                    }}
                  >
                    ✕
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {files.length > 0 && documentAttached && (
        <Button label="Ladda upp dokument" onClick={handleSubmitAttachments} />
      )}
    </div>
  );
};

export default AttachFile;

AttachFile.propTypes = {
  multiple: PropTypes.bool,
  onFilesChange: PropTypes.func,
  submitAttachment: PropTypes.func,
  documentAttached: PropTypes.bool,
  handleAttchDocument: PropTypes.func,
  submit: PropTypes.bool,
  handleSubmit: PropTypes.func,
};
